import { useEffect } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../../../../components/Form/controls/FormTextField.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";
import { createClaimFormDefinition } from "@accredo/fe-shared/services/claimFormService.js";

const validationSchema = yup.object({
  definition_file_name: yup.string().nullable(),
  definition: yup.string().nullable(),
});

function ClaimFormDefinitionEditContent({ claimForm, onSaved, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const currentDefinition = claimForm?.current_definition;

  return (
    <Form
      defaultValues={{
        definition_file_name: currentDefinition?.definition_file_name ?? "",
        definition: currentDefinition?.definition
          ? JSON.stringify(currentDefinition.definition, null, 2)
          : "",
      }}
      onSubmit={async (formData, methods) => {
        let parsedDefinition = null;
        if (formData.definition?.trim()) {
          try {
            parsedDefinition = JSON.parse(formData.definition);
          } catch (error) {
            methods.setError("definition", {
              type: "manual",
              message: "Ungültiges JSON",
            });
            return false;
          }
        }

        try {
          await createClaimFormDefinition(claimForm.id, {
            definition_file_name: formData.definition_file_name?.trim() || null,
            definition: parsedDefinition,
          });
          onSaved();
          onClose();
          return true;
        } catch (error) {
          enqueueSnackbar(
            error?.response?.data?.message ?? "Definition konnte nicht gespeichert werden",
            { variant: "error" }
          );
          return false;
        }
      }}
      resolver={yupResolver(validationSchema)}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 500 }}>
        <FormTextField name="definition_file_name" label="Datei-Pfad" fullWidth />
        <FormTextField
          name="definition"
          label="Definition (JSON)"
          fullWidth
          multiline
          minRows={14}
          maxRows={24}
          slotProps={{ input: { sx: { fontFamily: "monospace", fontSize: "0.8125rem" } } }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <SubmitButton variant="primary">Speichern</SubmitButton>
        </Box>
      </Box>
    </Form>
  );
}

export default function ClaimFormDefinitionEditModal({ open, onClose, claimForm, onSaved }) {
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    setOpenCD(true);
    setTca({
      title: `Definition bearbeiten: ${claimForm?.label ?? ""}`,
      maxWidth: "md",
      content: (
        <ClaimFormDefinitionEditContent claimForm={claimForm} onSaved={onSaved} onClose={onClose} />
      ),
      actions: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, claimForm?.id]);

  return <ContentDialog openCD={openCD} setOpenCD={setOpenCD} tca={tca} onClose={onClose} />;
}
